import { deleteApiJson, postApiJson, postJson } from "./api";
import { storage } from "../lib/storage";

/** Raw Telegram Login Widget payload (must be sent verbatim so the backend
 *  can verify the HMAC `hash`). */
export interface TelegramAuthData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface TelegramAuthResponse {
  status: "success" | "error";
  message?: string;
  user?: {
    id: number;
    name: string;
    telegram_id: number;
    username: string;
  };
  token?: string;
  errors?: Record<string, string[]>;
}

/**
 * Backend entry point for the Telegram OIDC ("Continue with Telegram") flow.
 * This must be a full browser navigation — it redirects to oauth.telegram.org
 * and back to the backend callback, which then returns to /auth/telegram/callback.
 */
export function telegramOidcUrl(): string {
  const base = import.meta.env.VITE_API_URL || "https://api.lotin.uz/api";
  return `${base.replace(/\/api\/?$/, "")}/auth/telegram/redirect`;
}

export async function telegramAuth(
  data: TelegramAuthData,
): Promise<TelegramAuthResponse> {
  try {
    // Send the full widget payload verbatim so the backend can verify the hash.
    const result: TelegramAuthResponse =
      await postApiJson<TelegramAuthResponse>("/auth/telegram", { ...data });

    if (
      result.status === "success" &&
      result.token &&
      typeof window !== "undefined"
    ) {
      storage.setItem("auth_token", result.token);
      if (result.user) {
        storage.setItem("auth_user", JSON.stringify(result.user));
      }
    }

    return result;
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Authentication failed",
    };
  }
}

/**
 * Telegram Mini App auth: send init_data to backend, get token and user (same storage as telegramAuth).
 * Used when the app is opened inside Telegram Web App (Mini App); no change to existing React auth logic.
 */
export async function telegramAuthByInitData(
  initData: string,
): Promise<TelegramAuthResponse> {
  try {
    const result: TelegramAuthResponse = await postJson<TelegramAuthResponse>(
      "/auth/telegram-mini-app",
      { init_data: initData },
    );

    if (
      result.status === "success" &&
      result.token &&
      typeof window !== "undefined"
    ) {
      storage.setItem("auth_token", result.token);
      if (result.user) {
        storage.setItem("auth_user", JSON.stringify(result.user));
      }
    }

    return result;
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Authentication failed",
    };
  }
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return storage.getItem("auth_token");
}

export function getAuthUser(): TelegramAuthResponse["user"] | null {
  if (typeof window === "undefined") return null;
  const userStr = storage.getItem("auth_user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

export function logout(): void {
  if (typeof window === "undefined") return;
  storage.removeItem("auth_token");
  storage.removeItem("auth_user");
}

export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false;
  return !!getAuthToken();
}

// --------------------------------------------------------------- email auth

export interface EmailAuthResponse {
  status: "success" | "error";
  message?: string;
  user?: {
    id: number;
    name: string;
    email?: string;
    telegram_id: number | null;
  };
  email_verified?: boolean;
  token?: string;
  errors?: Record<string, string[]>;
  // HTTP status of a failed request (0 = network/unreachable), so the UI can
  // pick a localized message instead of echoing a raw transport error.
  httpStatus?: number;
}

/** Persist token + user and notify the app (same as telegram flow). */
function storeSession(result: EmailAuthResponse): void {
  if (
    result.status === "success" &&
    result.token &&
    typeof window !== "undefined"
  ) {
    storage.setItem("auth_token", result.token);
    if (result.user) {
      storage.setItem("auth_user", JSON.stringify(result.user));
    }
    window.dispatchEvent(new Event("lotin:auth"));
  }
}

export async function register(data: {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<EmailAuthResponse> {
  try {
    const result = await postApiJson<EmailAuthResponse>("/auth/register", data);
    storeSession(result);
    return result;
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Registration failed",
    };
  }
}

export async function emailLogin(data: {
  email: string;
  password: string;
}): Promise<EmailAuthResponse> {
  try {
    const result = await postApiJson<EmailAuthResponse>("/auth/login", data);
    storeSession(result);
    return result;
  } catch (error) {
    return {
      status: "error",
      httpStatus: (error as { status?: number })?.status ?? 0,
      message: error instanceof Error ? error.message : "Login failed",
    };
  }
}

export async function forgotPassword(
  email: string,
): Promise<{ status: string; message?: string }> {
  try {
    return await postApiJson("/auth/forgot-password", { email });
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Request failed",
    };
  }
}

export async function resetPassword(data: {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<{ status: string; message?: string }> {
  try {
    return await postApiJson("/auth/reset-password", data);
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Reset failed",
    };
  }
}

export async function resendVerification(): Promise<{
  status: string;
  message?: string;
}> {
  try {
    return await postApiJson("/auth/email/resend", {});
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Request failed",
    };
  }
}

/** Permanently delete the account. Email accounts must pass their password. */
export async function deleteAccount(
  password?: string,
): Promise<{ status: string; message?: string }> {
  try {
    const res = await deleteApiJson<{ status: string; message?: string }>(
      "/auth/account",
      password ? { password } : undefined,
    );
    if (res.status === "success" && typeof window !== "undefined") {
      storage.removeItem("auth_token");
      storage.removeItem("auth_user");
      window.dispatchEvent(new Event("lotin:auth"));
    }
    return res;
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Delete failed",
    };
  }
}

/** Email verification via OTP code. */
export async function sendEmailCode(): Promise<{
  status: string;
  /** "email_undeliverable" when the mail provider refuses the address. */
  code?: string;
  message?: string;
}> {
  try {
    return await postApiJson("/auth/email/send-code", {});
  } catch (error) {
    return {
      status: "error",
      code: (error as any)?.body?.code,
      message: error instanceof Error ? error.message : "Request failed",
    };
  }
}

export async function verifyEmailCode(
  code: string,
): Promise<{ status: string; message?: string }> {
  try {
    return await postApiJson("/auth/email/verify-code", { code });
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Verification failed",
    };
  }
}
